import { Spinner, Box, HStack, Text, Input, Pressable, useMediaQuery, VStack, Button } from "native-base";
import { useEffect, useRef, useState } from "react";
import CustomButton from "../../../../components/button";
import NoDataFound from "../../../../components/noDataFound";
import CustomModal from "../../../../components/modal";
import MobileModelSheet from "../../../../components/actionSheet";
import { noPhotosUploadedSVG } from "../../../../utils/svg";
import { Toaster } from "../../../../components/toaster";
import { LoaderAnimation } from "../../../../components/spinner";
import { COLORS } from "../../../../utils/colors";
import { COMMONFONTSIZE, COMMONFONTWEIGHT } from "../../../../utils/fonts";
import { MOBILE_VIEW_WIDTH } from "../../../../utils/screens";
import Svg from 'react-native-svg';
import { getBenchmarkDataAPI, editBenchmarkData, updateBenchmarkFromInvoiceAPI } from "../../../../api/apis/quicklinks";
import BenchmarkEditModal from "./BenchmarkEditModal";
import AddNewBenchmarkModal from "./AddNewBenchmarkModal";
import UpdateBenchmarkModal from "./UpdateBenchmarkModal";
import PopOverModal from "../../../../components/popover";
import DividerLine from "../../../../components/divider";

const BenchmarkData = () => {
  const toaster = Toaster();
  const [MobileWindow] = useMediaQuery({ maxWidth: MOBILE_VIEW_WIDTH });

  // Benchmark data states
  const [benchmarkData, setBenchmarkData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [avgInvoiceEdits, setAvgInvoiceEdits] = useState({});
  const [avgInvoiceErrors, setAvgInvoiceErrors] = useState({});
  const [selectedAvgInvoiceIds, setSelectedAvgInvoiceIds] = useState({});
  const [isUpdatingAvgInvoices, setIsUpdatingAvgInvoices] = useState(false);
  const [isSelectingAll, setIsSelectingAll] = useState(false);
  const isFetchingRef = useRef(false);
  const scrollContainerRef = useRef(null);
  const [showConfirmUpdateModal, setShowConfirmUpdateModal] = useState(false);

  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const loaderCountRef = useRef(0);
  const didInitFetchRef = useRef(false);

  const showLoader = () => {
    if (loaderCountRef.current === 0) {
      LoaderAnimation.show();
    }
    loaderCountRef.current += 1;
  };

  const hideLoader = () => {
    if (loaderCountRef.current > 0) {
      loaderCountRef.current -= 1;
      if (loaderCountRef.current === 0) {
        LoaderAnimation.hide();
      }
    }
  };

  useEffect(() => {
    if (didInitFetchRef.current) return;
    didInitFetchRef.current = true;
    setIsLoading(true);
    getBenchmarkData(1, false);
  }, []);

  const checkAndLoadMore = () => {
    if (isLoadingData || !hasMore || isFetchingRef.current) return;

    const container = scrollContainerRef.current;
    if (container && container.scrollHeight > container.clientHeight) {
      const scrollTop = container.scrollTop;
      const containerHeight = container.clientHeight;
      const fullHeight = container.scrollHeight;

      if (scrollTop + containerHeight >= fullHeight - 200) {
        handleLoadMore();
      }
      return;
    }

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    const fullHeight = document.documentElement.scrollHeight;

    if (scrollTop + windowHeight >= fullHeight - 200) {
      handleLoadMore();
    }
  };

  useEffect(() => {
    const handleWindowScroll = () => checkAndLoadMore();
    window.addEventListener('scroll', handleWindowScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleWindowScroll);
  }, [hasMore, isLoadingData, currentPage]);

  useEffect(() => {
    if (isLoadingData || !hasMore) return;
    const rafId = requestAnimationFrame(() => checkAndLoadMore());
    return () => cancelAnimationFrame(rafId);
  }, [benchmarkData, isLoadingData, hasMore]);

  useEffect(() => {
    // Set loading to false after data is fetched (whether empty or not)
    if (!isLoadingData) {
      setIsLoading(false);
    }
  }, [isLoadingData]);

  const flattenBenchmarkData = (benchmarkDataList = []) => {
    const flattenedData = [];
    benchmarkDataList.forEach((headData) => {
      headData.sub_heads?.forEach((subHead) => {
        subHead.items?.forEach((item) => {
          flattenedData.push({
            head_id: headData._id,
            head: headData.head,
            sub_head_id: subHead.sub_head_id,
            sub_head: subHead.sub_head,
            item_id: item.item_id,
            tracking_id: item.tracking_id,
            details: item.details,
            size: item.size,
            quantity: item.qty,
            remarks: item.remarks,
            avg_invoice_value: item.avg_invoice_value,
            benchmark_price: item.benchmark_price,
            _id: `${headData._id}_${subHead.sub_head_id}_${item.item_id}`,
            originalData: {
              head_id: headData._id,
              sub_head_id: subHead.sub_head_id,
              item_id: item.item_id,
            }
          });
        });
      });
    });
    return flattenedData;
  };

  // Fetch benchmark data from API
  const getBenchmarkDataWithParams = async (page = 1, append = false, search = null) => {
    setIsLoadingData(true);
    if (!append) showLoader();
    
    // Clear data if not appending (for new search or first load)
    if (!append) {
      setBenchmarkData([]);
      setAvgInvoiceErrors({});
    }
    
    try {
      const searchParam = search !== null ? search : searchQuery.trim();
      const response = await getBenchmarkDataAPI(page, 5, searchParam);

      if (response.error) {
        toaster(response.message || 'Failed to fetch benchmark data', 'error', 'error');
        setPagination(null);
        setHasMore(false);
        return;
      }

      // Handle the actual response structure based on the API response
      const benchmarkDataList = response.data?.data?.data || [];
      const paginationData = response.data?.data?.pagination || null;
      const flattenedData = flattenBenchmarkData(benchmarkDataList);

      if (!append && paginationData?.pages > 1) {
        await loadAllBenchmarkRows(searchParam);
        return;
      }

      if (append) {
        setBenchmarkData(prev => [...prev, ...flattenedData]);
      } else {
        setBenchmarkData(flattenedData);
      }

      setAvgInvoiceEdits(prev => {
        const next = { ...prev };
        flattenedData.forEach((row) => {
          if (next[row._id] === undefined) {
            next[row._id] =
              row.avg_invoice_value === null || row.avg_invoice_value === undefined
                ? ""
                : row.avg_invoice_value.toString();
          }
        });
        return next;
      });

      setSelectedAvgInvoiceIds(prev => {
        const next = append ? { ...prev } : {};
        flattenedData.forEach((row) => {
          if (next[row._id] === undefined) {
            next[row._id] = row.avg_invoice_value !== null && row.avg_invoice_value !== undefined;
          }
        });
        return next;
      });

      // Update pagination state
      setPagination(paginationData);
      if (paginationData) {
        setHasMore(paginationData.page < paginationData.pages);
      } else {
        setHasMore(false);
      }

    } catch (error) {
      console.error('Error fetching benchmark data:', error);
      toaster('Error fetching benchmark data', 'error', 'error');
      setPagination(null);
      setHasMore(false);
    } finally {
      setIsLoadingData(false);
      if (!append) hideLoader();
    }
  };

  const getBenchmarkData = async (page = 1, append = false) => {
    await getBenchmarkDataWithParams(page, append, null);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    getBenchmarkDataWithParams(1, false, searchQuery.trim());
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setCurrentPage(1);
    getBenchmarkDataWithParams(1, false, '');
  };

  const handleLoadMore = () => {
    if (isLoadingData || !hasMore || isFetchingRef.current) return;
    isFetchingRef.current = true;
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    getBenchmarkData(nextPage, true).finally(() => {
      isFetchingRef.current = false;
    });
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const checkedIconSVG = (
    <Svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <rect x="3.5" y="3.5" width="17" height="17" rx="2" stroke="currentColor" strokeWidth="2" />
      <path
        d="M7.5 12L10.5 14.8L16.8 8.2"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );

  const uncheckedIconSVG = (
    <Svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <rect x="3.5" y="3.5" width="17" height="17" rx="2" stroke="currentColor" strokeWidth="2" />
    </Svg>
  );

  const infoIconSVG = (
    <Svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
      <path d="M12 10V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="12" cy="7" r="1.2" fill="currentColor" />
    </Svg>
  );

  const clearIconSVG = (
    <path
      d='M18 6L6 18M6 6L18 18'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  );

  const handleAvgInvoiceChange = (id, value) => {
    const normalized = normalizeText(value);
    const numericValue = normalized ? toNumber(normalized) : null;
    const isZeroValue = numericValue === 0;

    setAvgInvoiceErrors((prev) => ({ ...prev, [id]: isZeroValue }));
    setAvgInvoiceEdits((prev) => ({ ...prev, [id]: value }));
  };

  const toggleAvgInvoiceSelection = (id) => {
    setSelectedAvgInvoiceIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const totalSelectableCount = benchmarkData.length;
  const selectedCount = benchmarkData.reduce(
    (count, row) => (selectedAvgInvoiceIds[row._id] ? count + 1 : count),
    0
  );
  const allSelected = totalSelectableCount > 0 && selectedCount >= totalSelectableCount;

  useEffect(() => {
    setSelectedAvgInvoiceIds((prev) => {
      if (!benchmarkData.length) return {};
      const next = {};
      benchmarkData.forEach((row) => {
        const prevValue = prev[row._id];
        if (prevValue !== undefined) {
          next[row._id] = prevValue;
          return;
        }
        const hasPrefilled = row.avg_invoice_value !== null && row.avg_invoice_value !== undefined;
        if (hasPrefilled) next[row._id] = true;
      });
      return next;
    });
  }, [benchmarkData]);

  const syncAvgInvoiceEdits = (rows = []) => {
    setAvgInvoiceEdits((prev) => {
      const next = { ...prev };
      rows.forEach((row) => {
        if (next[row._id] === undefined) {
          next[row._id] =
            row.avg_invoice_value === null || row.avg_invoice_value === undefined
              ? ""
              : row.avg_invoice_value.toString();
        }
      });
      return next;
    });
  };

  async function loadAllBenchmarkRows(searchOverride = null) {
    const searchParam = searchOverride !== null ? searchOverride : searchQuery.trim();
    const perPage = 50;
    let page = 1;
    let totalPages = 1;
    let allRows = [];
    let latestPagination = null;

    try {
      do {
        const response = await getBenchmarkDataAPI(page, perPage, searchParam);
        const list = response.data?.data?.data || [];
        const paginationData = response.data?.data?.pagination || null;
        latestPagination = paginationData || latestPagination;
        totalPages = paginationData?.pages || 1;
        allRows = allRows.concat(flattenBenchmarkData(list));
        page += 1;
      } while (page <= totalPages);

      setBenchmarkData(allRows);
      if (latestPagination) {
        setPagination({ ...latestPagination, page: latestPagination.pages });
      } else {
        setPagination({ page: totalPages, pages: totalPages, total: allRows.length });
      }
      setHasMore(false);
      setCurrentPage(totalPages);
      syncAvgInvoiceEdits(allRows);
      return allRows;
    } catch (error) {
      console.error("Error fetching all benchmark data:", error);
      toaster("Failed to load all benchmark data", "error", "error");
      return null;
    }
  }

  const handleSelectAllToggle = async () => {
    if (!totalSelectableCount) return;
    if (isSelectingAll) return;

    if (selectedCount === totalSelectableCount) {
      setSelectedAvgInvoiceIds({});
      return;
    }

    setIsSelectingAll(true);
    const allRows = await loadAllBenchmarkRows();
    if (allRows && allRows.length) {
      const nextSelected = {};
      allRows.forEach((row) => {
        if (row?._id) nextSelected[row._id] = true;
      });
      setSelectedAvgInvoiceIds(nextSelected);
    }
    setIsSelectingAll(false);
  };

  const handleUpdateButtonPress = () => {
    if (!totalSelectableCount) return;
    if (selectedCount === 0) {
      toaster("Please select at least one Avg. Invoice to update", "error", "error");
      return;
    }
    setShowConfirmUpdateModal(true);
  };

  const handleUpdateSelectedAvgInvoices = async () => {
    const selectedIds = Object.keys(selectedAvgInvoiceIds).filter((id) => selectedAvgInvoiceIds[id]);
    if (!selectedIds.length) {
      toaster("Please select at least one Avg. Invoice to update", "error", "error");
      return;
    }

    const selectedRows = benchmarkData.filter((row) => selectedIds.includes(row._id));
    const invalidRow = selectedRows.find((row) => {
      const rawValue = normalizeText(avgInvoiceEdits[row._id]);
      if (!rawValue) return false;
      const numericValue = toNumber(rawValue);
      return numericValue === null || numericValue === 0;
    });
    if (invalidRow) {
      toaster("Please enter a valid Avg. Invoice value for the rows you updated", "error", "error");
      return;
    }

    setIsUpdatingAvgInvoices(true);
    showLoader();

    try {
      const items = selectedRows
        .map((row) => {
          const trackingId = normalizeText(row.tracking_id);
          if (!trackingId) return null;
          const rawValue = normalizeText(avgInvoiceEdits[row._id]);
          if (!rawValue) {
            return { tracking_id: trackingId };
          }
          const value = toNumber(rawValue);
          if (value === null) return null;
          const existingValue = row.avg_invoice_value;
          const existingNumber = existingValue === null || existingValue === undefined
            ? null
            : Number(existingValue);
          if (existingNumber !== null && Number.isFinite(existingNumber) && value === existingNumber) {
            return { tracking_id: trackingId };
          }
          return { tracking_id: trackingId, avg_invoice_value: value };
        })
        .filter(Boolean);

      if (!items.length) {
        toaster("Please select at least one Avg. Invoice to update", "error", "error");
        return;
      }

      const response = await updateBenchmarkFromInvoiceAPI({ items });
      if (response?.error) {
        toaster(response?.message || "Failed to update benchmark from invoice", "error", "error");
        return;
      }

      const successMessage =
        response?.data?.data?.message ||
        response?.message ||
        "Avg invoice values updated successfully";
      toaster(successMessage, "success", "success");
      setSelectedAvgInvoiceIds({});
      setCurrentPage(1);
      await getBenchmarkDataWithParams(1, false, searchQuery.trim());
    } catch (error) {
      console.error("Error updating benchmark from invoice:", error);
      toaster("Error updating benchmark from invoice", "error", "error");
    } finally {
      setIsUpdatingAvgInvoices(false);
      hideLoader();
    }
  };

  const closeConfirmUpdateModal = () => setShowConfirmUpdateModal(false);

  const getConfirmModalBody = () => (
    <VStack space={4} alignItems="center" px={1}>
      {noPhotosUploadedSVG}
      <Text
        fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
        fontWeight="medium"
        textAlign="center"
      >
        Are you sure you want to update with the new benchmark items you have selected?
      </Text>
    </VStack>
  );

  const getConfirmModalFooter = () => (
    <HStack justifyContent="flex-end" width="100%" space={4}>
      <CustomButton
        text="Cancel"
        onPress={closeConfirmUpdateModal}
        isOutline
        bgColor={COLORS.BLACK_COLOR}
        textColor={COLORS.BLACK_COLOR}
        disabled={isUpdatingAvgInvoices}
      />
      <CustomButton
        text={isUpdatingAvgInvoices ? "Updating..." : "Update"}
        onPress={() => {
          closeConfirmUpdateModal();
          handleUpdateSelectedAvgInvoices();
        }}
        isLoading={isUpdatingAvgInvoices}
      />
    </HStack>
  );

  // Handle close edit modal
  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedItem(null);
    setIsEditing(false);
  };

  // Handle edit benchmark data
  const handleEditBenchmark = async (updatedData) => {
    if (!selectedItem) return;

    setIsEditing(true);
    showLoader();

    try {
      // Use the original data structure for API call
      const { head_id, sub_head_id, item_id } = selectedItem.originalData || selectedItem;
      const updatePayload = {
        benchmark_id: head_id,
        sub_heads: [
          {
            sub_head_id,
            items: [
              {
                item_id,
                ...updatedData
              }
            ]
          }
        ]
      };

      const response = await editBenchmarkData(updatePayload);

      if (response.error) {
        toaster(response.message || 'Failed to update benchmark data', 'error', 'error');
        return;
      }

      toaster(response?.data?.data?.message || 'Benchmark data updated successfully', 'success', 'success');

      // Update the local state with the edited data
      const updatedBenchmarkData = benchmarkData.map(item =>
        item._id === selectedItem._id
          ? {
            ...item,
            quantity: updatedData.qty,
            benchmark_price: updatedData.benchmark_price,
            remarks: updatedData.remarks,
            avg_invoice_value: updatedData.avg_invoice_value
          }
          : item
      );
      setBenchmarkData(updatedBenchmarkData);

      closeEditModal();
    } catch (error) {
      console.error('Error updating benchmark data:', error);
      toaster('Error updating benchmark data', 'error', 'error');
    } finally {
      setIsEditing(false);
      hideLoader();
    }
  };

  // Helper function to determine if head should be displayed (only first occurrence)
  const shouldShowHead = (currentItem, index) => {
    if (index === 0) return true;
    const previousItem = benchmarkData[index - 1];
    return previousItem?.head_id !== currentItem.head_id;
  };

  // Helper function to determine if subhead should be displayed (only first occurrence in a head group)
  const shouldShowSubHead = (currentItem, index) => {
    if (index === 0) return true;
    const previousItem = benchmarkData[index - 1];
    return previousItem?.head_id !== currentItem.head_id ||
      previousItem?.sub_head_id !== currentItem.sub_head_id;
  };

  // Helper function to format price with comma separators
  const formatPrice = (price) => {
    if (!price && price !== 0) return '-';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return '-';
    return `₹ ${numPrice.toLocaleString('en-IN')}`;
  };

  const normalizeText = (value) => (value ?? '').toString().trim();

  const toNumber = (value) => {
    const normalized = normalizeText(value).replace(/,/g, '');
    if (!normalized) return null;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const buildAddBenchmarkPayloads = (newRows = []) => {
    const headMap = new Map();

    let lastHead = '';
    let lastSubHead = '';

    newRows.forEach((row) => {
      const rawHead = normalizeText(row?.head);
      const rawSubHead = normalizeText(row?.sub_head);
      const details = normalizeText(row?.details);
      const size = normalizeText(row?.size);
      const qty = normalizeText(row?.quantity);
      const price = toNumber(row?.price);
      const remarks = normalizeText(row?.remarks);

      const isTouchedRow =
        !!rawHead ||
        !!rawSubHead ||
        !!details ||
        !!size ||
        !!qty ||
        price !== null ||
        !!remarks;

      // Ignore default untouched rows from the initial 5 entries.
      if (!isTouchedRow) return;

      const head = rawHead || (row?.showHead === false ? lastHead : '');
      const subHead = rawSubHead || (row?.showSubHead === false ? lastSubHead : '');

      if (head) lastHead = head;
      if (subHead) lastSubHead = subHead;

      // Skip partially filled rows instead of blocking submit.
      if (!head || !subHead || !details) {
        return;
      }

      if (!headMap.has(head)) {
        headMap.set(head, new Map());
      }

      const subHeadMap = headMap.get(head);
      if (!subHeadMap.has(subHead)) {
        subHeadMap.set(subHead, []);
      }

      subHeadMap.get(subHead).push({
        details,
        benchmark_price: price,
        qty,
        size,
        remarks: remarks || null
      });
    });

    const payloads = Array.from(headMap.entries()).map(([head, subHeadMap]) => ({
      head,
      sub_heads: Array.from(subHeadMap.entries()).map(([sub_head, items]) => ({
        sub_head,
        items
      }))
    }));

    return { payloads };
  };

  const handleAddNewBenchmark = async (newRows) => {
    const { payloads } = buildAddBenchmarkPayloads(newRows);

    if (!payloads.length) {
      toaster('Please add at least one benchmark row before submitting', 'error', 'error');
      return;
    }

    setIsAdding(true);
    showLoader();

    try {
      let lastResponse = null;
      for (const payload of payloads) {
        const response = await editBenchmarkData(payload);
        lastResponse = response;
        if (response?.error) {
          toaster(response?.message || 'Failed to add benchmark data', 'error', 'error');
          return;
        }
      }

      const successMessage =
        lastResponse?.data?.data?.message || 'Benchmark data added successfully';
      toaster(successMessage, 'success', 'success');
      setShowAddModal(false);
      setCurrentPage(1);
      await getBenchmarkDataWithParams(1, false, searchQuery.trim());
    } catch (error) {
      console.error('Error adding benchmark data:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Error adding benchmark data';
      toaster(errorMessage, 'error', 'error');
    } finally {
      setIsAdding(false);
      hideLoader();
    }
  };

  const handleUpdateBenchmark = async (updatedRows) => {
    const rows = Array.isArray(updatedRows) ? updatedRows : [];

    const isRowTouched = (row = {}) =>
      !!normalizeText(row.heads) ||
      !!normalizeText(row.head_id) ||
      !!normalizeText(row.subHeads) ||
      !!normalizeText(row.sub_head_id) ||
      !!normalizeText(row.details) ||
      toNumber(row.price) !== null ||
      !!normalizeText(row.quantity) ||
      !!normalizeText(row.size) ||
      !!normalizeText(row.remarks);

    const resolveBenchmarkId = (headIdentifier) => {
      const normalizedHeadIdentifier = normalizeText(headIdentifier).toLowerCase();
      if (!normalizedHeadIdentifier) return "";

      const matchedHead = benchmarkData.find((item) => {
        const itemHeadId = normalizeText(item?.head_id).toLowerCase();
        const itemHead = normalizeText(item?.head).toLowerCase();
        return normalizedHeadIdentifier === itemHeadId || normalizedHeadIdentifier === itemHead;
      });

      return normalizeText(matchedHead?.head_id || headIdentifier);
    };

    const resolveSubHeadId = (headIdentifier, subHeadText, subHeadIdentifier) => {
      const normalizedHeadIdentifier = normalizeText(headIdentifier).toLowerCase();
      const normalizedSubHeadText = normalizeText(subHeadText).toLowerCase();
      const normalizedSubHeadIdentifier = normalizeText(subHeadIdentifier).toLowerCase();

      if (!normalizedHeadIdentifier || (!normalizedSubHeadText && !normalizedSubHeadIdentifier)) {
        return "";
      }

      // Prefer explicit sub_head_id coming from UpdateBenchmarkModal dropdown selection.
      if (normalizedSubHeadIdentifier && normalizedSubHeadIdentifier !== normalizedSubHeadText) {
        return normalizeText(subHeadIdentifier);
      }

      const matchedSubHead = benchmarkData.find((item) => {
        const itemHeadId = normalizeText(item?.head_id).toLowerCase();
        const itemHead = normalizeText(item?.head).toLowerCase();
        const itemSubHeadId = normalizeText(item?.sub_head_id).toLowerCase();
        const itemSubHead = normalizeText(item?.sub_head).toLowerCase();

        const headMatches = normalizedHeadIdentifier === itemHeadId || normalizedHeadIdentifier === itemHead;
        if (!headMatches) return false;

        return (
          (!!normalizedSubHeadIdentifier && normalizedSubHeadIdentifier === itemSubHeadId) ||
          (!!normalizedSubHeadText && normalizedSubHeadText === itemSubHead) ||
          (!!normalizedSubHeadText && normalizedSubHeadText === itemSubHeadId)
        );
      });

      return normalizeText(matchedSubHead?.sub_head_id || "");
    };

    const payloadByHeadId = new Map();
    let lastHeadId = "";
    let lastSubHead = "";
    let lastSubHeadId = "";
    let lastIsCustomSubHead = false;

    rows.forEach((row = {}) => {
      if (!isRowTouched(row)) return;

      const rawHeadId = normalizeText(row.head_id || (row.showHead === false ? lastHeadId : ""));
      const headId = resolveBenchmarkId(rawHeadId);
      const subHead = normalizeText(row.subHeads || (row.showSubHead === false ? lastSubHead : ""));
      const subHeadId = normalizeText(row.sub_head_id || (row.showSubHead === false ? lastSubHeadId : ""));
      const isCustomSubHead = typeof row.isCustomSubHead === "boolean"
        ? row.isCustomSubHead
        : (row.showSubHead === false ? lastIsCustomSubHead : false);
      const details = normalizeText(row.details);
      const benchmarkPrice = toNumber(row.price);
      const qty = normalizeText(row.quantity);
      const size = normalizeText(row.size);
      const remarks = normalizeText(row.remarks);

      if (headId) lastHeadId = headId;
      if (subHead) lastSubHead = subHead;
      if (subHeadId) lastSubHeadId = subHeadId;
      lastIsCustomSubHead = isCustomSubHead;

      // Skip incomplete rows silently (same pattern as add benchmark flow).
      if (!headId || !subHead || !details) return;

      if (!payloadByHeadId.has(headId)) {
        payloadByHeadId.set(headId, new Map());
      }

      const subHeadMap = payloadByHeadId.get(headId);
      const resolvedSubHeadId = isCustomSubHead
        ? ""
        : (subHeadId || resolveSubHeadId(headId, subHead, subHeadId));
      const useSubHeadId = !isCustomSubHead && !!resolvedSubHeadId;

      const subHeadKey = useSubHeadId ? `id:${resolvedSubHeadId}` : `name:${subHead}`;
      if (!subHeadMap.has(subHeadKey)) {
        subHeadMap.set(subHeadKey, {
          ...(useSubHeadId ? { sub_head_id: resolvedSubHeadId } : { sub_head: subHead }),
          items: [],
        });
      }

      subHeadMap.get(subHeadKey).items.push({
        details,
        benchmark_price: benchmarkPrice,
        qty,
        size,
        remarks: remarks || null,
        avg_invoice_value: null,
      });
    });

    const payloads = Array.from(payloadByHeadId.entries()).map(([benchmark_id, subHeadMap]) => ({
      benchmark_id,
      sub_heads: Array.from(subHeadMap.values()).filter((subHead) => subHead.items?.length > 0),
    })).filter((payload) => payload.sub_heads.length > 0);

    if (!payloads.length) {
      toaster("Please add at least one valid benchmark row before submitting", "error", "error");
      return;
    }

    setIsUpdating(true);
    showLoader();
    try {
      let lastResponse = null;
      for (const payload of payloads) {
        const response = await editBenchmarkData(payload);
        lastResponse = response;
        if (response?.error) {
          toaster(response?.message || "Failed to update benchmark data", "error", "error");
          return;
        }
      }

      toaster(lastResponse?.data?.data?.message || "Benchmark data updated successfully", "success", "success");
      setShowUpdateModal(false);
      setCurrentPage(1);
      await getBenchmarkDataWithParams(1, false, searchQuery.trim());
    } catch (error) {
      console.error("Error updating benchmark data:", error);
      toaster(error?.response?.data?.message || error?.message || "Error updating benchmark data", "error", "error");
    } finally {
      setIsUpdating(false);
      hideLoader();
    }
  };

  return (
    <>
      {isLoading ? (
        <Box flex={1} alignItems="center" justifyContent="center">
          <Spinner color={COLORS.THEME_COLOR} size="lg" />
        </Box>
      ) : (
        <Box ref={scrollContainerRef} onScroll={checkAndLoadMore}>
          {/* Header, Search and Add Button */}
          <HStack justifyContent="end" alignItems="center" mb={4} flexWrap={MobileWindow ? "wrap" : "nowrap"}>
            <HStack space={3} alignItems="center" flexWrap={MobileWindow ? "wrap" : "nowrap"}>
              {(benchmarkData?.length > 0 || searchQuery.trim()) && (
                <Box position='relative'>
                  <Input
                    placeholder='Search'
                    value={searchQuery}
                    onChangeText={(text) => {
                      const trimmedText = text?.trimStart();
                      setSearchQuery(trimmedText);
                      if (!trimmedText) {
                        setCurrentPage(1);
                        getBenchmarkDataWithParams(1, false, '');
                      }
                    }}
                    onKeyPress={handleKeyPress}
                    isLoading={isLoadingData}
                    width={MobileWindow ? '200px' : '300px'}
                    border={0}
                    bg={COLORS.WHITE_COLOR}
                    _focus={{ borderColor: 'transparent' }}
                    fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                    paddingRight={searchQuery ? '40px' : '12px'}
                  />
                  {searchQuery && (
                    <Pressable
                      position='absolute'
                      right={2}
                      top={0}
                      bottom={0}
                      justifyContent='center'
                      alignItems='center'
                      onPress={handleClearSearch}
                      p={1}
                      borderRadius={4}
                      _pressed={{ bg: COLORS.BORDER_COLOR }}
                    >
                      <Svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
                        {clearIconSVG}
                      </Svg>
                    </Pressable>
                  )}
                </Box>
              )}

              <PopOverModal
                isOpen={isPopoverOpen}
                onClose={() => setIsPopoverOpen(false)}
                onOpenPress={() => setIsPopoverOpen(true)}
                button={true}
                svg={
                  <Text
                    color={COLORS.WHITE_COLOR}
                    fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                    fontWeight={COMMONFONTWEIGHT.LARGE_FONT_WEIGHT}
                  >
                    Modify Benchmark
                  </Text>
                }
                body={
                  <VStack space={3} p={2}>
                    <Text
                      onPress={() => {
                        setIsPopoverOpen(false);
                        scrollToTop();
                        setShowAddModal(true);
                      }}
                      color={COLORS.BLACK_COLOR}
                      fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                      fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                      style={{ cursor: 'pointer' }}
                    >
                      Add New Benchmark
                    </Text>
                    <DividerLine direction="horizontal" width="100%" />
                    <Text
                      onPress={() => {
                        setIsPopoverOpen(false);
                        scrollToTop();
                        setShowUpdateModal(true);
                      }}
                      color={COLORS.BLACK_COLOR}
                      fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                      fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                      style={{ cursor: 'pointer' }}
                    >
                      Update Existing Benchmark
                    </Text>
                  </VStack>
                }
              />
              {selectedCount > 0 && (
                <Button
                  colorScheme="none"
                  bgColor={COLORS.THEME_COLOR}
                  textColor={COLORS.WHITE_COLOR}
                  fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                  fontWeight={COMMONFONTWEIGHT.LARGE_FONT_WEIGHT}
                  px={4}
                  py="10px"
                  borderRadius="100px"
                  isLoading={isUpdatingAvgInvoices || isSelectingAll}
                  onPress={handleUpdateButtonPress}
                  minW="230px"
                  justifyContent="center"
                  style={{ whiteSpace: "nowrap" }}
                >
                  <Text
                    color={COLORS.WHITE_COLOR}
                    fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                    fontWeight={COMMONFONTWEIGHT.LARGE_FONT_WEIGHT}
                    numberOfLines={1}
                  >
                    Update New Benchmark ({selectedCount})
                  </Text>
                </Button>
              )}
            </HStack>
          </HStack>

          {/* Benchmark Data Table */}
          {benchmarkData?.length > 0 ? (
            <Box bg={COLORS.WHITE_COLOR} borderRadius={8} borderWidth={1} borderColor={COLORS.BORDER_COLOR} overflow="hidden">
              <Box overflowX="auto">
                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                  <thead>
                    <tr style={{ backgroundColor: COLORS.BADGE_GRAY_COLOR }}>
                      <th style={{ padding: '12px', textAlign: 'left', verticalAlign: 'middle', fontWeight: 'bold', width: '14%' }}>Head</th>
                      <th style={{ padding: '12px', textAlign: 'left', verticalAlign: 'middle', fontWeight: 'bold', width: '13%' }}>Sub Head</th>
                      <th style={{ padding: '12px', textAlign: 'left', verticalAlign: 'middle', fontWeight: 'bold', width: '9%' }}>Tracking ID</th>
                      <th style={{ padding: '12px', textAlign: 'left', verticalAlign: 'middle', fontWeight: 'bold', width: '20%' }}>Details</th>
                      <th style={{ padding: '12px', textAlign: 'left', verticalAlign: 'middle', fontWeight: 'bold', width: '8%' }}>Size</th>
                      <th style={{ padding: '12px', textAlign: 'left', verticalAlign: 'middle', fontWeight: 'bold', width: '8%' }}>Quantity</th>
                      <th style={{ padding: '12px', textAlign: 'left', verticalAlign: 'middle', fontWeight: 'bold', width: '9%' }}>Current Benchmark</th>
                      <th style={{ padding: '12px', textAlign: 'left', verticalAlign: 'middle', fontWeight: 'bold', width: '9%' }}>New Benchmark</th>
                      <th style={{ padding: '12px', textAlign: 'left', verticalAlign: 'middle', fontWeight: 'bold', width: '10%' }}>Remarks</th>
                      <th style={{ padding: '12px', textAlign: 'left', verticalAlign: 'middle', fontWeight: 'bold', width: '8%' }}>
                        <HStack alignItems="center" justifyContent="space-between">
                          <Text fontWeight="bold" fontSize="inherit">Actions</Text>
                          <Box width="24px" display="flex" alignItems="center" justifyContent="center">
                            <Pressable
                              onPress={handleSelectAllToggle}
                              disabled={isSelectingAll}
                              color={allSelected ? COLORS.THEME_COLOR : COLORS.BORDER_COLOR}
                              _hover={{ color: COLORS.THEME_COLOR }}
                              style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                            >
                              {allSelected ? checkedIconSVG : uncheckedIconSVG}
                            </Pressable>
                          </Box>
                        </HStack>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {benchmarkData.map((item, index) => (
                      <tr key={`${item?._id || 'benchmark-row'}_${index}`} style={{ borderBottom: `1px solid ${COLORS.BORDER_COLOR}` }}>
                        <td style={{ padding: '12px', textAlign: 'left', verticalAlign: 'middle' }}>
                          {shouldShowHead(item, index) ? (item.head || '-') : ''}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'left', verticalAlign: 'middle' }}>
                          {shouldShowSubHead(item, index) ? (item.sub_head || '-') : ''}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'left', verticalAlign: 'middle'}}>
                          {item.tracking_id || '-'}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'left', verticalAlign: 'middle' }}>
                          {item.details || '-'}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'left', verticalAlign: 'middle' }}>
                          {item.size || '-'}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'left', verticalAlign: 'middle' }}>
                          {item.quantity || '-'}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'left', verticalAlign: 'middle', fontWeight: 'bold' }}>
                          {formatPrice(item.benchmark_price)}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'left', verticalAlign: 'middle', fontWeight: 'bold' }}>
                          <VStack space={1}>
                            <Input
                              value={avgInvoiceEdits[item._id] ?? ""}
                              onChangeText={(text) => handleAvgInvoiceChange(item._id, text)}
                              size="md"
                              bg={COLORS.WHITE_COLOR}
                              borderColor={avgInvoiceErrors[item._id] ? COLORS.ERROR_TOAST_COLOR : COLORS.BORDER_COLOR}
                              _focus={{
                                borderColor: avgInvoiceErrors[item._id] ? COLORS.ERROR_TOAST_COLOR : COLORS.THEME_COLOR
                              }}
                              keyboardType="numeric"
                            />
                            {avgInvoiceErrors[item._id] ? (
                              <HStack alignItems="center" space={1} mt={0.5}>
                                <Box color={COLORS.ERROR_TOAST_COLOR}>
                                  {infoIconSVG}
                                </Box>
                                <Text
                                  fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                                  color={COLORS.ERROR_TOAST_COLOR}
                                  lineHeight={COMMONFONTSIZE.MEDIUM_FONT_SIZE + 4}
                                >
                                  Invalid
                                </Text>
                              </HStack>
                            ) : null}
                          </VStack>
                        </td>
                        <td style={{ padding: '12px', textAlign: 'left', verticalAlign: 'middle' }}>
                          {item.remarks || '-'}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'left', verticalAlign: 'middle' }}>
                          <Box width="24px" display="flex" alignItems="center" justifyContent="center" marginLeft="auto">
                            <Pressable
                              onPress={() => toggleAvgInvoiceSelection(item._id)}
                              color={selectedAvgInvoiceIds[item._id] ? COLORS.THEME_COLOR : COLORS.BORDER_COLOR}
                              _hover={{ color: COLORS.THEME_COLOR }}
                              style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                            >
                              {selectedAvgInvoiceIds[item._id] ? checkedIconSVG : uncheckedIconSVG}
                            </Pressable>
                          </Box>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>

              {isLoadingData && (
                <Box p={8} textAlign="center">
                  <Spinner color={COLORS.THEME_COLOR} size="lg" />
                </Box>
              )}
            </Box>
          ) : benchmarkData?.length === 0 && !isLoadingData ? (
            <NoDataFound dataName="benchmark data" />
          ) : null}

          {/* Pagination Info */}
          {pagination && benchmarkData.length > 0 && (
            <Box mt={4} textAlign="center">
              <Text fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE} color={COLORS.PARTNERS_HEADING_COLOR}>
                Page {pagination.page} of {pagination.pages} • Total {pagination.total} Heads
              </Text>
            </Box>
          )}

          {/* Load More Button */}
          {/* {hasMore && benchmarkData.length > 0 && !isLoadingData && (
            <Box mt={4} display="flex" justifyContent="center" alignSelf={'center'}>
              <CustomButton
                text='Load More'
                onPress={handleLoadMore}
                isLoading={isLoadingData}
              />
            </Box>
          )} */}
        </Box>
      )}

      {/* Edit Modal */}
      <BenchmarkEditModal
        isOpen={showEditModal}
        onClose={closeEditModal}
        editData={selectedItem}
        onSubmit={handleEditBenchmark}
        isEditing={isEditing}
      />

      <AddNewBenchmarkModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddNewBenchmark}
        isLoading={isAdding}
      />

      <UpdateBenchmarkModal
        isOpen={showUpdateModal}
        onClose={() => setShowUpdateModal(false)}
        onSubmit={handleUpdateBenchmark}
        isLoading={isUpdating}
      />

      {!MobileWindow ? (
        <CustomModal
          isOpen={showConfirmUpdateModal}
          onClose={closeConfirmUpdateModal}
          header="Update New Benchmark"
          body={getConfirmModalBody()}
          footer={getConfirmModalFooter()}
          width="40%"
        />
      ) : (
        <MobileModelSheet
          isOpen={showConfirmUpdateModal}
          onClose={closeConfirmUpdateModal}
          header="Update New Benchmark"
          body={getConfirmModalBody()}
          footer={getConfirmModalFooter()}
          maxH="50vh"
        />
      )}
    </>
  );
};

export default BenchmarkData; 
